<script>
  import { t } from '../i18n/index.js';
  import { companyInvoicesApi } from '../stores/api.js';
  import { previewKey, currentPreview } from '../lib/number-preview.js';

  /**
   * The document number, as the operator meets it: the number the server will
   * assign, previewed, and a checkbox to type one by hand for recovery or
   * migration. The preview reserves nothing — the number actually assigned is
   * the next free one when the document is issued, and the parent shows *that*
   * one from the server's answer.
   */
  export let companyId;
  export let draftId = null;
  export let tipoDocumento = 'TD01';
  export let formatoTrasmissione = 'FPR12';
  export let invoiceDate = null;
  export let disabled = false;
  export let manual = false;
  export let invoiceNumber = '';

  // The last preview together with the inputs it was worked out for. What is
  // shown is `preview`, which is empty while those are not the inputs on the
  // form: a number for the old company, year or series must not stay on screen
  // as if it were the next one while the new one is being fetched.
  let shown = null;
  let previewError = null;
  let loading = false;
  let requestSeq = 0;

  $: key = previewKey(companyId, draftId, tipoDocumento, formatoTrasmissione, invoiceDate);
  $: preview = currentPreview(shown, key);
  $: if (companyId) refresh(companyId, draftId, tipoDocumento, formatoTrasmissione, invoiceDate);

  async function refresh(cid, did, tipo, formato, date) {
    const seq = ++requestSeq;
    const askedFor = previewKey(cid, did, tipo, formato, date);
    loading = true;
    previewError = null;
    try {
      const result = await companyInvoicesApi.nextNumber(cid, {
        draftId: did, tipoDocumento: tipo, formatoTrasmissione: formato, invoiceDate: date,
      });
      if (seq === requestSeq) shown = { key: askedFor, value: result };
    } catch (err) {
      if (seq === requestSeq) { shown = null; previewError = err; }
    } finally {
      if (seq === requestSeq) loading = false;
    }
  }
</script>

<div>
  {#if !manual}
    <p class="label">{$t('numbering.previewLabel')}</p>
    {#if loading && !preview}
      <p class="text-sm text-muted">{$t('numbering.previewLoading')}</p>
    {:else if preview}
      <p class="font-mono font-bold text-lg" style="color: var(--color-text-body);">{preview.invoiceNumber}</p>
      <p class="mt-1 text-xs text-muted measure">{$t('numbering.previewHint', { serie: preview.prefix, anno: preview.year })}</p>
    {:else if previewError?.code === 'series_exhausted'}
      <!-- The server's own Italian sentence: apiCall() puts it in `message` and
           only flags its presence in `serverMessage`. -->
      <p class="mt-1 text-xs measure" style="color: var(--color-text-body);">{previewError.message}</p>
    {:else if previewError}
      <p class="mt-1 text-xs text-muted measure">{$t('numbering.previewUnavailable')}</p>
    {/if}
  {:else}
    <label class="label" for="invoice-number-manual">{$t('numbering.manualLabel')}</label>
    <input id="invoice-number-manual" type="text" class="input" maxlength="20" bind:value={invoiceNumber} {disabled}
      placeholder={preview ? $t('numbering.manualPlaceholder', { esempio: preview.invoiceNumber }) : ''} />
  {/if}

  <label class="mt-3 flex items-start gap-2 text-xs" style="color: var(--color-text-body);">
    <input type="checkbox" bind:checked={manual} {disabled} />
    <span>{$t('numbering.manualToggle')}</span>
  </label>
  {#if manual}
    <p class="mt-1 text-xs text-muted measure">{$t('numbering.manualHint')}</p>
  {/if}
</div>
